import React, { useCallback } from "react";
import cx from "classnames";
import { Button, Em, Flex, Text } from "@radix-ui/themes";
import { CsatType } from "../entities/ChatSettingsV2";
import { CSATComment } from "./CSATComment";

interface ScoreItem {
  level: "low" | "medium" | "high";
  label: string;
  score: number;
  hint?: string;
  hintAlign?: "left" | "center" | "right";
}

const scoreItems: Record<CsatType, ScoreItem[]> = {
  rating: [
    {
      level: "low",
      label: "😢",
      score: 1,
      hint: "Very dissatisfied",
      hintAlign: "left",
    },
    {
      level: "medium",
      label: "🙁",
      score: 2,
    },
    {
      level: "medium",
      label: "😐",
      score: 3,
      hint: "Neutral",
      hintAlign: "center",
    },
    {
      level: "medium",
      label: "🙂",
      score: 4,
    },
    {
      level: "high",
      label: "😊",
      score: 5,
      hint: "Very satisfied",
      hintAlign: "right",
    },
  ],
  numeric_rating: [
    {
      level: "low",
      label: "1",
      score: 1,
      hint: "Very dissatisfied",
      hintAlign: "left",
    },
    { level: "medium", label: "2", score: 2 },
    {
      level: "medium",
      label: "3",
      score: 3,
      hint: "Neutral",
      hintAlign: "center",
    },
    { level: "medium", label: "4", score: 4 },
    {
      level: "high",
      label: "5",
      score: 5,
      hint: "Very satisfied",
      hintAlign: "right",
    },
  ],
  binary: [
    { level: "high", label: "👍", score: 5 },
    { level: "low", label: "👎", score: 1 },
  ],
};

interface ScoreButtonProps extends ScoreItem {
  onClick: (score: number) => void;
  checked?: boolean;
}

function ScoreButton({
  level,
  label,
  score,
  onClick,
  hint,
  hintAlign = "center",
  checked = false,
}: ScoreButtonProps) {
  const hasHint = hint != null;

  const handleClick = useCallback(() => {
    onClick(score);
  }, [onClick, score]);

  return (
    <Button
      size={"1"}
      variant={"outline"}
      color={"gray"}
      className={cx(
        "qc-Chat__CSAT__ScoreButton",
        `qc-Chat__CSAT__ScoreButton-level-${level}`,
        {
          "qc-Chat__CSAT__ScoreButton-has-hint": hasHint,
        }
      )}
      onClick={handleClick}
      role="radio"
      aria-label={score.toString()}
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
    >
      {label}

      {hasHint && (
        <div
          className={`qc-Chat__CSAT__ScoreButton__Hint  qc-Chat__CSAT__ScoreButton__Hint-aling-${hintAlign}`}
        >
          {hint}
        </div>
      )}
    </Button>
  );
}

interface CSATProps {
  csatType: CsatType;
  allowCsatComment?: boolean;
  csatCommentText?: string;
  csatScore?: number;
  csatComment?: string;
  onCSATScore: (score: number, comment?: string) => void;
  onScrollToBottom: () => void;
}

export function CSAT({
  csatType,
  csatScore,
  csatComment,
  allowCsatComment,
  csatCommentText,
  onCSATScore,
  onScrollToBottom,
}: CSATProps) {
  const handleCSATComment = useCallback(
    (comment: string) => {
      if (csatScore == null) {
        return;
      }

      onCSATScore(csatScore, comment);
    },
    [csatScore, onCSATScore]
  );

  const onCSATCommentShow = useCallback(() => {
    requestAnimationFrame(onScrollToBottom);
  }, [onScrollToBottom]);

  return (
    <Flex
      pt={"6"}
      px={"1"}
      gap={"10px"}
      direction={"column"}
      role="radiogroup"
      aria-labelledby="csat-label"
    >
      <Text
        size={"2"}
        color={"gray"}
        weight={"medium"}
        highContrast
        id="csat-label"
      >
        How satisfied were you with our service today
      </Text>

      <Flex gap={"10px"}>
        {scoreItems[csatType].map((item) => (
          <ScoreButton
            key={item.score}
            {...item}
            onClick={onCSATScore}
            checked={item.score === csatScore}
          />
        ))}
      </Flex>

      <Text size={"2"} color={"gray"} aria-live="polite">
        {csatScore != null && <Em>Thank you for your feedback</Em>}
      </Text>

      {csatScore != null && allowCsatComment && csatComment == null && (
        <CSATComment
          csatCommentText={csatCommentText}
          onCSATComment={handleCSATComment}
          onShow={onCSATCommentShow}
        />
      )}
    </Flex>
  );
}
